import React, { createContext, useContext, useState, useCallback } from 'react';
import labels, { Language, Labels } from './labels';

interface LanguageContextType {
  lang: Language;
  t: Labels;
  setLanguage: (lang: Language) => void;
  toggleLanguage: () => void;
  locale: string;
}

const LanguageContext = createContext<LanguageContextType>({
  lang: 'sv',
  t: labels.sv,
  setLanguage: () => {},
  toggleLanguage: () => {},
  locale: 'sv-SE',
});

export { LanguageContext };
export const useLanguage = () => useContext(LanguageContext);

const LOCALE_MAP: Record<Language, string> = {
  sv: 'sv-SE',
  en: 'en-US',
};

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [lang, setLang] = useState<Language>(() => {
    const stored = localStorage.getItem('language');
    return (stored === 'en' || stored === 'sv') ? stored : 'sv';
  });

  const setLanguage = useCallback((newLang: Language) => {
    setLang(newLang);
    localStorage.setItem('language', newLang);
  }, []);

  const toggleLanguage = useCallback(() => {
    const newLang = lang === 'sv' ? 'en' : 'sv';
    setLanguage(newLang);
  }, [lang, setLanguage]);

  const value: LanguageContextType = {
    lang,
    t: labels[lang],
    setLanguage,
    toggleLanguage,
    locale: LOCALE_MAP[lang],
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
